import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { LOGOUT, SET_CHOOSEN_TABLE } from "../constans/reducerActions";
import { WIZUALIZACJE } from "../constans/tableCalls";
import { useStateValue } from "../stateProvider/stateProvider";
import Header from "../elements/Header/Header";
import {
  buildDashboardActions,
  DASHBOARD_CHART_COLORS,
  DASHBOARD_FORMAT_POOL,
  loadDashboardCountsAndFormats,
  loadHomeProfile,
  loadRandomAnimatedPreviewFrames,
  loadRandomAnimatedPreviewFramesForFormat,
} from "./homeController";
import "./Home.css";

const PIE_CHART_SIZE = 180;
const PIE_VIEWBOX_SIZE = 220;
const PIE_CENTER = PIE_VIEWBOX_SIZE / 2;
const PIE_RADIUS = 104;
const MIN_SEGMENT_RATIO = 0.01;
const CHART_EXTENDED_COLORS = [
  "#e85511",
  "#f08d19",
  "#d94a0b",
  "#c64100",
  "#f7a22c",
  "#f4c14f",
  "#e7b24a",
  "#d8a03c",
  "#c58a2f",
  "#b53b2d",
  "#d34c3f",
  "#9f2f24",
  "#7d2219",
  "#2f2a28",
  "#3d3734",
  "#55504c",
  "#6b6762",
  "#8a857e",
  "#a8a198",
  "#cfc7bb",
  "#e9dfd1",
  "#f2e9db",
  "#e6d8c6",
  "#d7c1a4",
  "#caa884",
  "#b8885a",
  "#a87447",
  "#99633a",
  "#8b562f",
  "#7a4b2a",
];

const formatTotal = (value) => new Intl.NumberFormat("pl-PL").format(value || 0);

const createColorSeed = (value) => {
  const text = String(value || "");
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash << 5) - hash + text.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

const getRandomFormatColor = (format, attempt = 0) => {
  const seed = createColorSeed(`${format}-${attempt}`);
  const index = (seed + attempt * 13) % CHART_EXTENDED_COLORS.length;
  return CHART_EXTENDED_COLORS[index];
};

const mergeSmallSegments = (formats) => {
  const normalized = (formats || [])
    .map((entry) => ({
      format: String(entry?.format || "").trim(),
      count: Number(entry?.count) || 0,
    }))
    .filter((entry) => entry.format && entry.count > 0);

  const total = normalized.reduce((sum, entry) => sum + entry.count, 0);
  if (!total) return [];

  const main = [];
  let otherCount = 0;

  normalized.forEach((entry) => {
    if (entry.count / total < MIN_SEGMENT_RATIO) {
      otherCount += entry.count;
    } else {
      main.push(entry);
    }
  });

  if (otherCount > 0) {
    main.push({ format: "Inne", count: otherCount, isOther: true });
  }

  return main;
};

const mapChartSegments = (formats) => {
  const mergedFormats = mergeSmallSegments(formats);
  const usedColors = new Set();

  return mergedFormats.map((entry, index) => {
    let color = "";

    if (entry.isOther) {
      color = "#9aa0a6";
    } else if (index < DASHBOARD_CHART_COLORS.length) {
      color = DASHBOARD_CHART_COLORS[index];
    } else {
      let attempt = 0;
      do {
        color = getRandomFormatColor(entry.format, attempt);
        attempt += 1;
      } while (usedColors.has(color) && attempt < 20);
    }

    usedColors.add(color);

    return {
      format: entry.format,
      count: entry.count,
      color,
    };
  });
};

const polarToCartesian = (centerX, centerY, radius, angleDeg) => {
  const angleRad = (angleDeg * Math.PI) / 180;
  return {
    x: centerX + radius * Math.cos(angleRad),
    y: centerY + radius * Math.sin(angleRad),
  };
};

const fullCirclePath = (centerX, centerY, radius) =>
  [
    `M ${centerX} ${centerY - radius}`,
    `A ${radius} ${radius} 0 1 1 ${centerX - 0.01} ${centerY - radius}`,
    `A ${radius} ${radius} 0 1 1 ${centerX} ${centerY - radius}`,
    "Z",
  ].join(" ");

const createPieSlices = (segments, total) => {
  if (!total) return [];
  if (segments.length === 1) {
    return [
      {
        ...segments[0],
        path: fullCirclePath(PIE_CENTER, PIE_CENTER, PIE_RADIUS),
      },
    ];
  }

  let currentAngle = -90;
  return segments.map((segment) => {
    const angle = (segment.count / total) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    const start = polarToCartesian(PIE_CENTER, PIE_CENTER, PIE_RADIUS, startAngle);
    const end = polarToCartesian(PIE_CENTER, PIE_CENTER, PIE_RADIUS, endAngle);
    const largeArcFlag = angle > 180 ? 1 : 0;
    const path = [
      `M ${PIE_CENTER} ${PIE_CENTER}`,
      `L ${start.x} ${start.y}`,
      `A ${PIE_RADIUS} ${PIE_RADIUS} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`,
      "Z",
    ].join(" ");

    currentAngle = endAngle;

    return {
      ...segment,
      path,
    };
  });
};

const PieChartCard = ({ title, formats }) => {
  const [hoveredFormat, setHoveredFormat] = useState("");
  const segments = mapChartSegments(formats);
  const total = segments.reduce((sum, segment) => sum + segment.count, 0);
  const slices = createPieSlices(segments, total);

  return (
    <article className="home-dashboard__panel-card home-dashboard__panel-card--chart">
      <header className="home-dashboard__panel-header">
        <h3>{title}</h3>
        <span>{formatTotal(total)} szt.</span>
      </header>
      {total > 0 ? (
        <div className="home-dashboard__chart-grid">
          <div className="home-dashboard__pie-wrapper">
            <div
              className="home-dashboard__pie"
              style={{
                width: PIE_CHART_SIZE,
                height: PIE_CHART_SIZE,
              }}
            >
              <svg
                className="home-dashboard__pie-svg"
                viewBox={`0 0 ${PIE_VIEWBOX_SIZE} ${PIE_VIEWBOX_SIZE}`}
                onMouseLeave={() => setHoveredFormat("")}
              >
                <circle
                  className="home-dashboard__pie-track"
                  cx={PIE_CENTER}
                  cy={PIE_CENTER}
                  r={PIE_RADIUS}
                  fill="none"
                  strokeWidth="1"
                />
                {slices.map((slice) => (
                  <path
                    key={slice.format}
                    d={slice.path}
                    fill={slice.color}
                    stroke="#ffffff"
                    strokeWidth="2"
                    className={`home-dashboard__pie-slice ${
                      hoveredFormat && hoveredFormat !== slice.format ? "is-dimmed" : ""
                    }`}
                    onMouseEnter={() => setHoveredFormat(slice.format)}
                  >
                    <title>{`${slice.format}: ${formatTotal(slice.count)} szt.`}</title>
                  </path>
                ))}
              </svg>
            </div>
            <p className="home-dashboard__chart-hover">
              {hoveredFormat ? `Format: ${hoveredFormat}` : "Najedz na segment wykresu"}
            </p>
          </div>
          <ul className="home-dashboard__legend">
            {segments.map((segment) => (
              <li key={segment.format}>
                <span
                  className="home-dashboard__legend-dot"
                  style={{ backgroundColor: segment.color }}
                />
                <span className="home-dashboard__legend-label">{segment.format}</span>
                <span className="home-dashboard__legend-value">{formatTotal(segment.count)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="home-dashboard__empty">Brak danych dla wykresu.</p>
      )}
    </article>
  );
};

const PreviewFrameCard = ({ frame }) => (
  <article className="home-dashboard__preview-card">
    <header>
      <h4>{frame.format}</h4>
      <p>{frame.sourceTitle}</p>
    </header>
    {frame.link ? (
      <iframe
        className="home-dashboard__preview-iframe"
        src={frame.link}
        title={`preview-${frame.format}`}
        width="300"
        height="600"
      />
    ) : (
      <div className="home-dashboard__preview-empty">Brak kreacji dla tego formatu.</div>
    )}
  </article>
);

const ActionList = ({ actions, onActionClick }) => (
  <div className="home-dashboard__action-list">
    {actions.map((action) => (
      <button
        key={action.id}
        type="button"
        className={`home-dashboard__action-btn ${action.isActive ? "is-active" : ""}`}
        onClick={() => onActionClick(action)}
      >
        <span className="home-dashboard__action-icon">{action.icon}</span>
        <span className="home-dashboard__action-copy">
          <strong>{action.label}</strong>
          <span>{action.description}</span>
        </span>
      </button>
    ))}
  </div>
);

const Home = () => {
  const navigate = useNavigate();
  const [reducer, dispatch] = useStateValue();
  const [profile, setProfile] = useState({ name: "User", img: "./Images/userIco.png" });
  const [counts, setCounts] = useState({
    visualCount: 0,
    codedCount: 0,
    visualFormats: [],
    codedFormats: [],
  });
  const [frames, setFrames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingFrames, setLoadingFrames] = useState(true);
  const [error, setError] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedPreviewFormat, setSelectedPreviewFormat] = useState("");
  const [previewModeLabel, setPreviewModeLabel] = useState("");

  const currentPanelLabel =
    reducer.choosenTable === WIZUALIZACJE ? "Kreacje statyczne" : "Kreacje zakodowane";

  const handleSelectTable = (tableName, explicitPath) => {
    if (tableName) {
      dispatch({
        type: SET_CHOOSEN_TABLE,
        item: tableName,
      });
      navigate("/panel");
      return;
    }

    navigate(explicitPath || "/");
  };

  const handleLogout = () => {
    dispatch({ type: LOGOUT });
    navigate("/login");
  };

  const actions = buildDashboardActions({
    role: reducer.role,
    choosenTable: reducer.choosenTable,
    onSelectTable: handleSelectTable,
    onLogout: handleLogout,
  });

  const loadMixedPreviewFrames = useCallback(async () => {
    setLoadingFrames(true);
    try {
      const nextFrames = await loadRandomAnimatedPreviewFrames(DASHBOARD_FORMAT_POOL, 3);
      setFrames(nextFrames);
      setPreviewModeLabel("Tryb: pelne losowanie roznych dostepnych formatow");
    } catch (refreshError) {
      setFrames([]);
      setPreviewModeLabel("Nie udalo sie pobrac podgladow.");
    } finally {
      setLoadingFrames(false);
    }
  }, []);

  const handleDrawBySelectedFormat = useCallback(async () => {
    const canonicalFormat = String(selectedPreviewFormat || "").trim();
    if (!canonicalFormat) {
      setPreviewModeLabel("Wybierz format, aby wylosowac 3 kreacje.");
      return;
    }
    if (!DASHBOARD_FORMAT_POOL.includes(canonicalFormat)) {
      setPreviewModeLabel("Wybrany format jest niedozwolony.");
      return;
    }

    setLoadingFrames(true);
    try {
      const nextFrames = await loadRandomAnimatedPreviewFramesForFormat(canonicalFormat, 3);
      setFrames(nextFrames);
      setPreviewModeLabel(`Tryb: 3 losowe kreacje dla formatu ${canonicalFormat}`);
    } catch (drawError) {
      setFrames([]);
      setPreviewModeLabel("Nie udalo sie pobrac podgladow dla wybranego formatu.");
    } finally {
      setLoadingFrames(false);
    }
  }, [selectedPreviewFormat]);

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      setError("");

      try {
        const [profileData, countData] = await Promise.all([
          loadHomeProfile(),
          loadDashboardCountsAndFormats(),
        ]);

        setProfile(profileData);
        setCounts(countData);
      } catch (loadError) {
        setError("Nie udalo sie pobrac danych dashboardu.");
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  useEffect(() => {
    loadMixedPreviewFrames();
  }, [loadMixedPreviewFrames]);

  const onActionClick = (action) => {
    setIsMobileMenuOpen(false);
    action.onClick();
  };

  return (
    <>
      <Header name={profile.name} img={profile.img} />

      <section className="home-dashboard">
        <main className="home-dashboard__main">
          <div className="home-dashboard__main-header">
            <div className="home-dashboard__main-title">
              <h1>Panel główny</h1>
              <span className="home-dashboard__main-title-line" />
              <p>Aktualny panel: {currentPanelLabel}</p>
            </div>
            <button
              type="button"
              className="home-dashboard__hamburger"
              onClick={() => setIsMobileMenuOpen((prevState) => !prevState)}
              aria-label="Otworz menu funkcji"
            >
              <img src="/Images/hamburger.png" alt="Menu" />
            </button>
          </div>

          <div className="home-dashboard__cards-row">
            <article className="home-dashboard__count-card home-dashboard__count-card--orange">
              <p>Kreacje zakodowane</p>
              <strong>{formatTotal(counts.codedCount)}</strong>
            </article>
            <article className="home-dashboard__count-card home-dashboard__count-card--blue">
              <p>Wizualizacje</p>
              <strong>{formatTotal(counts.visualCount)}</strong>
            </article>
          </div>

          {loading ? <p className="home-dashboard__status">Ladowanie danych...</p> : null}
          {error ? <p className="home-dashboard__status home-dashboard__status--error">{error}</p> : null}

          <div className="home-dashboard__charts-row">
            <PieChartCard title="Zakodowane - liczba per format" formats={counts.codedFormats} />
            <PieChartCard title="Wizualizacje - liczba per format" formats={counts.visualFormats} />
          </div>

          <div className="home-dashboard__previews-header">
            <h2>Losowe podglady (animowane)</h2>
            <div className="home-dashboard__preview-controls">
              <select
                value={selectedPreviewFormat}
                onChange={(event) => setSelectedPreviewFormat(event.target.value)}
              >
                <option value="">Wybierz format</option>
                {DASHBOARD_FORMAT_POOL.map((format) => (
                  <option key={format} value={format}>
                    {format}
                  </option>
                ))}
              </select>
              <button type="button" onClick={handleDrawBySelectedFormat}>
                Losuj 3 z formatu
              </button>
              <button type="button" onClick={loadMixedPreviewFrames}>
                Pelne losowanie
              </button>
            </div>
          </div>
          {previewModeLabel ? <p className="home-dashboard__preview-mode">{previewModeLabel}</p> : null}

          {loadingFrames ? (
            <p className="home-dashboard__status">Ladowanie podgladow...</p>
          ) : (
            <div className="home-dashboard__previews-grid">
              {frames.map((frame) => (
                <PreviewFrameCard key={`${frame.format}-${frame.link || "empty"}`} frame={frame} />
              ))}
            </div>
          )}
        </main>

        <aside className="home-dashboard__sidebar">
          <div className="home-dashboard__sidebar-title">
            <h3>Panel Sterowania</h3>
            <span className="home-dashboard__sidebar-title-line" />
          </div>
          <ActionList actions={actions} onActionClick={onActionClick} />
        </aside>
      </section>

      <div
        className={`home-dashboard__mobile-backdrop ${isMobileMenuOpen ? "is-open" : ""}`}
        onClick={() => setIsMobileMenuOpen(false)}
      />
      <aside className={`home-dashboard__mobile-drawer ${isMobileMenuOpen ? "is-open" : ""}`}>
        <header>
          <h3>Menu funkcji</h3>
          <button type="button" onClick={() => setIsMobileMenuOpen(false)} aria-label="Zamknij menu">
            Zamknij
          </button>
        </header>
        <ActionList actions={actions} onActionClick={onActionClick} />
      </aside>
    </>
  );
};

export default Home;
